// SESSION SPD1 (scale run, 30 October 2026): the page asks for LESS. Each tab reads only its OWN list, and as the queue row (view=queue: a
// reference, a site, the facts the warning pills and the summary read, and the send state; about a sixth of the bytes of the whole record), and
// the other tabs' counts come from GET /api/reporting/counts (one statement) instead of loading the other list whole to count it. A THIRD
// TAB, Sent today, lists the reports whose send date is today in London, AND any report recorded today as sent from Gmail whatever date it
// carries, so the batch just done is still in front of the person; a record of a send from Gmail can be cleared there (the existing clear,
// two presses). And Ready to send gains "Record as sent outside Guvnor": for the reports the person already sent from Gmail by hand, the
// same ticks and the same recipients check, then ONE confirmed press that names the count and the date and records them (POST
// /api/jobsheets/sent-outside-batch, in runs of at most 50, which runs the single record route's own implementation once per report). The box
// that does it is drawn AFTER the list, so the addresses it vouches for (each row's, as checked) are above it. It SENDS NOTHING. Everything
// below is as SC1 and SC7A built it.
//
// SESSION SC7A (the calm menu): the two stages are now two TABS of the Servicing hub, Awaiting release (/office/jobsheets/release) and Ready to
// send (/office/jobsheets/release?stage=ready_to_send), drawn by the hub's own tab strip (src/lib/nav.ts, src/components/HubTabs.tsx). The stage is
// read from the address, so a link, a refresh and the old URL all land on the right one; the page no longer draws a second pair of tabs of its own.
// A stage change in the address is ignored while a run is in progress, so a half-finished batch is never re-pointed at the other list.
//
// Office "Release and send" queue (route /office/jobsheets/release). Session SC1 (scale run, spec gap 1,
// docs/ELE-V2-SCALE-TARGET-SPEC-2026-10-22.md): at 1,000 lifts about 20 closed visits a day reach the office, and since the
// release gate (Sessions BEG and BEK) each one is two presses on two screens, one record at a time. This page lists a day's work at
// each stage of the gate so the office checks the summary, ticks and acts once per batch.
//
// IT DECIDES NOTHING NEW. Releasing is the existing per-job approve route (POST /api/jobsheets/:jobId/approve, the record page's
// "Release for sending") and sending is the existing per-job send-to-client route (the record page's "Send now"), each called one
// job at a time, in order, so every refusal those routes make (not released, no customer email, an erased recipient, a render
// failure, Gmail not connected) is reported on that job's own row and stops nothing else. Nothing is sent without a person pressing
// the two-step confirm that names the From address and the count, after a recipients check that shows every To and Cc (D-MAIL-1,
// the release gate and the draft-first rule are unchanged). The full record is one click away on every row.
//
// WHAT THE SEND STAGE SENDS. It sends the visit's record or report from the job sheet (service record, repair report, technician
// visit certificate, or the call-out's completion certificate), exactly what the record page's Send now sends. A call-out's separate
// ATTENDANCE report (CallOut.reportSentAt, "Release call-out report") is a different document with its own release on the call-out
// page, and nothing here touches it.
//
// THE LIST IS RE-READ BEFORE IT IS ACTED ON. A colleague can mark a report as sent outside Guvnor, resend it or send it from chat
// after this page loaded, and none of those writes anything the send preview checks. So "Check recipients" and the send itself each
// re-fetch the Ready to send list first and drop any ticked visit that has left it, saying so on its row.
//
// THE PREVIEW IS A PROMISE. Each previewed To and Cc is what is sent, and a preview is discarded the moment the selection or the
// list changes (a tick, a refresh, a tab), so the second press can never pick up an address nobody looked at.
//
// A RUN STOPS WITH THE PAGE. Leaving the page mid-run stops the loop before its next call (the calls already made are on the
// records), and the links away are inert while a run is in progress so the person cannot walk off from a half-finished batch.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import type { JobSheetListItem, ReportSendPreview } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import JobTypeBadge from "../../components/JobTypeBadge";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { formatDate, formatDateTime } from "../../lib/format";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useNavigationLock } from "../../lib/navigationLock";

export type ReleaseQueueStage = "awaiting_release" | "ready_to_send" | "sent_today";

/** A row's own result. `warnings` are the route's warning-class notes, shown amber and never green. */
type RowResult = { ok: boolean; note: string; warnings?: string[] };
type Preview = ReportSendPreview | { error: string };
type QueueRow = JobSheetListItem & { jobId: string };

/** What the office should look at before releasing without opening the record. Pure, so it is unit tested. */
export function releaseWarnings(s: JobSheetListItem): string[] {
  const w: string[] = [];
  const status = (s.liftStatusOnLeaving ?? "").toLowerCase();
  if (s.equipmentOutOfService || status.includes("out of service")) w.push("Left out of service");
  else if (status && !status.includes("running normally")) w.push(s.liftStatusOnLeaving as string);
  if (s.defectsCount > 0) w.push(s.defectsCount === 1 ? "1 fault recorded" : `${s.defectsCount} faults recorded`);
  if (s.riskAssessmentMissing) w.push("No risk assessment");
  if (s.lateReason) w.push(`Sent in late: ${s.lateReason}`);
  return w;
}

/** The green line the office reads after a release. The route's own note starts "Released.", so that is not said twice. */
export function releaseResultNote(r: { alreadyApproved?: boolean; draftNote?: string | null }): string {
  if (r.alreadyApproved) return "Already released.";
  const rest = (r.draftNote ?? "").replace(/^\s*Released\.?\s*/i, "").trim();
  return rest ? `Released. ${rest}` : "Released.";
}

/**
 * The route's warning-class notes, shown on the row in amber and never green: hours that reached no timesheet, the invoice note (which
 * carries the stranded-estimate safety note and the "invoice by hand" cases) and any defects the route offers to resolve.
 */
export function releaseResultWarnings(r: { alreadyApproved?: boolean; invoiceNote?: string | null; timesheetNote?: string | null; defectsToReview?: unknown[] }): string[] {
  if (r.alreadyApproved) return [];
  const w: string[] = [];
  if (r.timesheetNote) w.push(r.timesheetNote);
  if (r.invoiceNote) w.push(r.invoiceNote);
  if (r.defectsToReview && r.defectsToReview.length) w.push(`${r.defectsToReview.length} defect(s) to review: open the record.`);
  return w;
}

/** The most reports the batch route takes in one request (SENT_OUTSIDE_BATCH_MAX on the server). A bigger selection goes in runs of this size. */
export const BATCH_RUN = 50;

const NO_LONGER_WAITING ="No longer waiting: it was sent or changed.";
const SENDING_MARKER = "Marked as sending: check Sent mail on the record.";
const REMEDIAL_BLOCK = "Release on the record: faults need the repair offer.";

/**
 * Why a visit cannot be ticked in this stage, or null. Pure, so it is unit tested.
 *  - Awaiting release: a visit that open defects were raised to fix is released on the record. The approve route offers to resolve
 *    those defects only in its first answer, which the record page shows, so a batch release would lose the offer.
 *  - Ready to send: a sheet stamped as sent to the client with no recipient recorded is mid-send, or sent with the recipient not
 *    written, or a failed send whose roll-back did not land. The queue cannot tell which, so it will not send it again.
 */
export function batchBlockReason(stage: ReleaseQueueStage, s: Pick<JobSheetListItem, "openRemedialDefects" | "sentToClientAt" | "emailSentTo">): string | null {
  if (stage === "sent_today") return null;
  if (stage === "awaiting_release") return (s.openRemedialDefects ?? 0) > 0 ? REMEDIAL_BLOCK : null;
  return s.sentToClientAt != null && !s.emailSentTo ? SENDING_MARKER : null;
}

function summaryText(s: JobSheetListItem): string | null {
  return s.workCarriedOut || s.faultFound || s.engineerNotes || s.advisoryNotes || null;
}

/** The value of a datetime-local input for an instant, in the browser's own time (the office's, London). Pure. */
export function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The send state of a row on the Sent today tab, in words. Pure.
 *
 * A row is on this tab because Guvnor sent it today, or because a record of a send from Gmail was made today with any date on it. The words
 * follow which of the two the sheet's own send stamp (sentToClientAt) belongs to: the record is the latest send only when the stamp IS the
 * record's date. When a send from Guvnor came after it, that send is what the stamp, the addressee and the time describe, and the record is
 * named after it. A record is described by the date the person GAVE ("dated"), which may not be today.
 */
export function sentTodayLine(s: Pick<JobSheetListItem, "sentOutside" | "emailSentTo" | "emailSentRecipients" | "sentToClientAt">): string {
  const o = s.sentOutside;
  const copyWords = (cc: string[]) => (cc.length ? `, copy ${cc.join(", ")}` : "");
  const recordWords = (r: NonNullable<JobSheetListItem["sentOutside"]>) =>
    `Recorded${r.recordedByName ? ` by ${r.recordedByName}` : ""} as sent from Gmail, outside Guvnor, dated ${formatDateTime(r.sentAt)}, to ${r.to.join(", ")}${copyWords(r.cc)}.`;
  const outsideIsLatest = !!o && (s.sentToClientAt == null || new Date(s.sentToClientAt).getTime() === new Date(o.sentAt).getTime());
  if (o && outsideIsLatest) return recordWords(o);
  const rec = s.emailSentRecipients;
  const to = rec?.to?.length ? rec.to : s.emailSentTo ? [s.emailSentTo] : [];
  const cc = rec?.cc ?? [];
  const at = s.sentToClientAt ? ` at ${formatDateTime(s.sentToClientAt)}` : "";
  const platform = to.length ? `Sent from Guvnor${at}, to ${to.join(", ")}${copyWords(cc)}.` : `Sent${at}.`;
  return o ? `${platform} ${recordWords(o)}` : platform;
}

const isPreviewError = (p: Preview | undefined): p is { error: string } => !!p && "error" in p;
// A preview that says the report was already sent (and unchanged) sends nothing, so it is left out of the count the person confirms.
const isSendable = (p: Preview | undefined): p is ReportSendPreview => !!p && !isPreviewError(p) && !p.alreadySent;

/** The two release-queue counts, or null when they cannot be read (a tab count is a convenience, never a reason to stop the page). */
async function readReleaseCounts(): Promise<{ sheetsAwaitingRelease: number; reportsReadyToSend: number } | null> {
  try {
    const c = await api.reporting.releaseCounts();
    const r = c?.release;
    return r && typeof r.sheetsAwaitingRelease === "number" && typeof r.reportsReadyToSend === "number" ? r : null;
  } catch {
    return null;
  }
}

export function ReleaseQueuePage() {
  // The layout is chosen once at mount (the house pattern, src/lib/mobileLayout.ts): cards on a phone, never a desktop table.
  const [mobile] = useState(initialMobileLayout);
  const [params] = useSearchParams();
  const stageParam = params.get("stage");
  const urlStage: ReleaseQueueStage = stageParam === "ready_to_send" ? "ready_to_send" : stageParam === "sent_today" ? "sent_today" : "awaiting_release";
  const [stage, setStage] = useState<ReleaseQueueStage>(urlStage);
  const [rows, setRows] = useState<JobSheetListItem[]>([]);
  // The job ids the server's latest answer for this stage contained. A row kept only to show its result is not in it.
  const [liveIds, setLiveIds] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState<Record<ReleaseQueueStage, number | null>>({ awaiting_release: null, ready_to_send: null, sent_today: null });
  // When the person says the reports were sent from Gmail (a datetime-local value). Defaults to now; the server refuses a future time.
  const [sentAtLocal, setSentAtLocal] = useState(() => localInputValue(new Date()));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [busy, setBusy] = useState(false);
  // The report whose record is being cleared on the Sent today tab: only ITS button reads "Clearing...", the others are just inactive.
  const [clearingId, setClearingId] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  // Leaving mid-run would wipe the per-row results or abandon a half-finished batch: the hub strip and the menu are inert until it is done.
  useNavigationLock(busy || loading);
  // Send previews, one per selected job, loaded only when the person asks to check recipients.
  const [previews, setPreviews] = useState<Record<string, Preview> | null>(null);
  // Bumped by anything that makes a held preview untrustworthy (a tick, a refresh, a tab, a reload). A recipients check that was
  // started before the bump is thrown away when it lands.
  const generation = useRef(0);
  // A token per list load: a slower, older answer can never overwrite a newer one.
  const loadToken = useRef(0);
  // Cleared on unmount: a run that is in the middle of its loop stops calling the routes once the page is gone.
  const mounted = useRef(true);
  const resultsRef = useRef<Record<string, RowResult>>({});

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const putResult = useCallback((id: string, r: RowResult) => {
    resultsRef.current = { ...resultsRef.current, [id]: r };
    setResults(resultsRef.current);
  }, []);
  const clearResults = useCallback(() => {
    resultsRef.current = {};
    setResults({});
  }, []);

  const discardPreviews = useCallback(() => {
    generation.current += 1;
    setPreviews(null);
  }, []);

  /** Adopt a freshly read list: replace the rows, keep a row that already shows a result from this visit to the page. */
  const adoptList = useCallback((fresh: JobSheetListItem[]) => {
    const ids = new Set(fresh.map((r) => r.jobId).filter((x): x is string => !!x));
    setLiveIds(ids);
    setRows((prev) => [...fresh, ...prev.filter((r) => r.jobId && !ids.has(r.jobId) && resultsRef.current[r.jobId])]);
  }, []);

  const load = useCallback(
    async (which: ReleaseQueueStage) => {
      const token = ++loadToken.current;
      setLoading(true);
      setLoadError(false);
      setBatchError(null);
      setSelected(new Set());
      discardPreviews();
      try {
        // THIS tab's list only (the queue row), and the other tabs' counts from the count route. A count that cannot be read is simply not
        // shown: the list itself is what the person acts on.
        const [cur, release] = await Promise.all([api.jobsheets.list({ queue: which, view: "queue" }), readReleaseCounts()]);
        if (token !== loadToken.current || !mounted.current) return;
        adoptList(cur);
        setCounts({
          awaiting_release: which === "awaiting_release" ? cur.length : (release?.sheetsAwaitingRelease ?? null),
          ready_to_send: which === "ready_to_send" ? cur.length : (release?.reportsReadyToSend ?? null),
          sent_today: which === "sent_today" ? cur.length : null,
        });
        setLoading(false);
      } catch {
        if (token !== loadToken.current || !mounted.current) return;
        setLoadError(true);
        setLoading(false);
      }
    },
    [discardPreviews, adoptList],
  );

  useEffect(() => {
    void load(stage);
  }, [load, stage]);

  // The hub's tab strip changes the address; follow it unless a run is in progress.
  useEffect(() => {
    if (busy || urlStage === stage) return;
    clearResults();
    setStage(urlStage);
  }, [urlStage, stage, busy, clearResults]);

  const withId = useMemo(() => rows.filter((r): r is QueueRow => !!r.jobId), [rows]);
  // A row can be ticked only while it is still in the list the server gave, has not been done here, and is not held back.
  const selectable = useMemo(
    () => withId.filter((r) => liveIds.has(r.jobId) && !results[r.jobId]?.ok && batchBlockReason(stage, r) == null),
    [withId, liveIds, results, stage],
  );
  const allTicked = selectable.length > 0 && selectable.every((r) => selected.has(r.jobId));
  const chosen = selectable.filter((r) => selected.has(r.jobId));

  function toggle(id: string) {
    discardPreviews();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    discardPreviews();
    setSelected(allTicked ? new Set() : new Set(selectable.map((r) => r.jobId)));
  }

  function toggleOpen(id: string) {
    setOpen((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function dropFromCount(stageDone: ReleaseQueueStage, n: number) {
    if (n > 0) setCounts((c) => ({ ...c, [stageDone]: c[stageDone] == null ? null : Math.max(0, (c[stageDone] as number) - n) }));
  }

  async function releaseChosen() {
    setBusy(true);
    setBatchError(null);
    let n = 0;
    let done = 0;
    for (const r of chosen) {
      if (!mounted.current) return;
      n += 1;
      setProgress(`Releasing ${n} of ${chosen.length}`);
      try {
        const res = await api.jobsheets.approve(r.jobId);
        putResult(r.jobId, { ok: true, note: releaseResultNote(res), warnings: releaseResultWarnings(res) });
        done += 1;
      } catch (err) {
        putResult(r.jobId, { ok: false, note: friendlyApiError(err, "Could not release this visit. Open the record to see why.") });
      }
    }
    if (!mounted.current) return;
    setProgress(null);
    setBusy(false);
    setSelected(new Set());
    dropFromCount("awaiting_release", done);
  }

  /** Read the Ready to send list again. Null (with a message) when it cannot be read, in which case nothing is checked or sent. */
  async function freshReadyList(quiet = false): Promise<JobSheetListItem[] | null> {
    try {
      const fresh = await api.jobsheets.list({ queue: "ready_to_send", view: "queue" });
      if (!mounted.current) return null;
      adoptList(fresh);
      setCounts((c) => ({ ...c, ready_to_send: fresh.length }));
      return fresh;
    } catch {
      // `quiet` is for the read made AFTER an unanswered record: a failure to read again must not replace the words already on the page.
      if (mounted.current && !quiet) setBatchError("The list could not be refreshed, so nothing was checked or sent. Try again.");
      return null;
    }
  }

  /** A ticked report the batch left out because its recipients check did not come back clean, said on its own row (the previews are discarded after). */
  function noteLeftOut(ticked: QueueRow[], allPreviews: Record<string, Preview>) {
    for (const r of ticked) {
      const p = allPreviews[r.jobId];
      if (isPreviewError(p)) putResult(r.jobId, { ok: false, note: `Left out of this batch. Its recipients check said: ${p.error}` });
      else if (p && !isSendable(p)) putResult(r.jobId, { ok: false, note: "Left out of this batch: it was already sent earlier." });
    }
  }

  /** The ticked visits that are still waiting and not held back; every other one is marked on its own row. */
  function keepStillWaiting(fresh: JobSheetListItem[], ticked: QueueRow[], verb: "Refreshed." | "Skipped."): QueueRow[] {
    const byId = new Map(fresh.map((f) => [f.jobId, f]));
    const keep: QueueRow[] = [];
    for (const r of ticked) {
      const f = byId.get(r.jobId);
      const held = f ? batchBlockReason("ready_to_send", f) : null;
      if (!f) putResult(r.jobId, { ok: false, note: `${NO_LONGER_WAITING} ${verb}` });
      else if (held) putResult(r.jobId, { ok: false, note: `${held} ${verb}` });
      else keep.push({ ...f, jobId: r.jobId });
    }
    return keep;
  }

  async function checkRecipients() {
    const startedAt = generation.current;
    setBusy(true);
    setBatchError(null);
    setProgress("Refreshing the list");
    const fresh = await freshReadyList();
    if (!fresh) {
      if (mounted.current) {
        setProgress(null);
        setBusy(false);
      }
      return;
    }
    const toCheck = keepStillWaiting(fresh, chosen, "Refreshed.");
    const out: Record<string, Preview> = {};
    let n = 0;
    for (const r of toCheck) {
      if (!mounted.current) return;
      n += 1;
      setProgress(`Checking recipients ${n} of ${toCheck.length}`);
      try {
        out[r.jobId] = await api.jobsheets.sendToClientPreview(r.jobId);
      } catch (err) {
        out[r.jobId] = { error: friendlyApiError(err, "This report cannot be sent from here. Open the record.") };
      }
    }
    if (!mounted.current) return;
    setProgress(null);
    setBusy(false);
    // The list or the selection moved while this ran: what was checked is no longer what is selected, so it is thrown away.
    if (generation.current !== startedAt) return;
    setSelected(new Set(toCheck.map((r) => r.jobId)));
    setPreviews(out);
    // The date the record box offers is NOW, as the check finishes: a tab left open overnight must not offer yesterday's time.
    setSentAtLocal(localInputValue(new Date()));
  }

  const sendable = previews ? chosen.filter((r) => isSendable(previews[r.jobId])) : [];
  const fromAddresses = previews
    ? Array.from(new Set(sendable.map((r) => (previews[r.jobId] as ReportSendPreview).from).filter((f): f is string => !!f)))
    : [];

  async function sendChosen() {
    if (!previews) return;
    setBusy(true);
    setBatchError(null);
    setProgress("Refreshing the list");
    // Immediately before the first send: a colleague may have sent or recorded these since they were checked.
    const fresh = await freshReadyList();
    if (!fresh) {
      if (mounted.current) {
        setProgress(null);
        setBusy(false);
      }
      return;
    }
    const toSend = keepStillWaiting(fresh, sendable, "Skipped.");
    noteLeftOut(chosen, previews);
    let n = 0;
    let done = 0;
    for (const r of toSend) {
      if (!mounted.current) return;
      n += 1;
      setProgress(`Sending ${n} of ${toSend.length}`);
      const p = previews[r.jobId] as ReportSendPreview;
      try {
        // EXACTLY the To and Cc the person looked at, never a re-resolved address: the route checks each one again.
        const res = await api.jobsheets.sendToClient(r.jobId, { to: p.to, cc: p.cc });
        if (res.alreadySent) {
          putResult(r.jobId, { ok: true, note: "Already sent earlier, so nothing was sent now." });
        } else {
          const to = res.to ?? p.to;
          const cc = res.cc ?? p.cc;
          putResult(r.jobId, { ok: true, note: `Sent to ${to.join(", ")}${cc.length ? `, copy ${cc.join(", ")}` : ""}.` });
          done += 1;
        }
      } catch (err) {
        putResult(r.jobId, { ok: false, note: friendlyApiError(err, "Could not send this report. Open the record to try again.") });
      }
    }
    if (!mounted.current) return;
    setProgress(null);
    setBusy(false);
    setSelected(new Set());
    discardPreviews();
    dropFromCount("ready_to_send", done);
  }

  /** The date the person gave, as the words the confirm and the row results use. */
  const sentAtWords = (() => {
    const d = new Date(sentAtLocal);
    return Number.isNaN(d.getTime()) ? null : formatDateTime(d);
  })();

  /**
   * RECORD THE CHECKED REPORTS AS SENT OUTSIDE GUVNOR. For the reports the person already sent from Gmail by hand. It sends NOTHING. It uses
   * exactly the To and Cc the recipients check showed (the addresses on file), so the record says who the report went to as the person saw it;
   * a report that went to someone else is recorded on its own record, where the addresses are typed. The list is re-read first, like the send,
   * and the batch route judges each report with the single route's own rules.
   */
  async function recordOutsideChosen() {
    if (!previews) return;
    const sentAt = new Date(sentAtLocal);
    if (Number.isNaN(sentAt.getTime())) {
      setBatchError("Give the date and time they were sent from Gmail.");
      return;
    }
    setBusy(true);
    setBatchError(null);
    setProgress("Refreshing the list");
    const fresh = await freshReadyList();
    if (!fresh) {
      if (mounted.current) {
        setProgress(null);
        setBusy(false);
      }
      return;
    }
    const toRecord = keepStillWaiting(fresh, sendable, "Skipped.");
    noteLeftOut(chosen, previews);
    let done = 0;
    // The route takes at most BATCH_RUN reports, so a bigger selection (a backlog) goes in runs of that size and the results are merged per
    // report. A run the server REFUSED stops the rest, with its own words (nothing in it was changed). A run that gave NO ANSWER stops the
    // rest too, and nothing is claimed about it: the server may have recorded some, so the list is read again and the person is told so.
    let unanswered = false;
    for (let from = 0; from < toRecord.length && mounted.current; from += BATCH_RUN) {
      const run = toRecord.slice(from, from + BATCH_RUN);
      setProgress(toRecord.length > BATCH_RUN ? `Recording ${from + 1} to ${from + run.length} of ${toRecord.length}` : `Recording ${run.length}`);
      try {
        const res = await api.jobsheets.markSentOutsideBatch({
          sentAt: sentAt.toISOString(),
          items: run.map((r) => {
            const p = previews[r.jobId] as ReportSendPreview;
            return { jobId: r.jobId, to: p.to, cc: p.cc };
          }),
        });
        if (!mounted.current) return;
        for (const item of res.results) {
          if (item.ok) {
            putResult(item.jobId, { ok: true, note: `Recorded as sent from Gmail, outside Guvnor, dated ${formatDateTime(sentAt)}. Nothing was emailed. It is on the Sent today tab, where the record can be cleared.` });
            done += 1;
          } else {
            putResult(item.jobId, { ok: false, note: item.message ?? "Could not record this report. Open the record to see why." });
          }
        }
      } catch (err) {
        if (!mounted.current) return;
        const said = friendlyApiError(err, "");
        if (said) {
          // The server answered with a refusal and its own sentence: its words stand.
          setBatchError(said);
        } else {
          unanswered = true;
          setBatchError("The answer did not come back, so some of these may have been recorded. Refresh the list to see what was recorded.");
        }
        break;
      }
    }
    if (unanswered) await freshReadyList(true);
    if (!mounted.current) return;
    setProgress(null);
    setBusy(false);
    setSelected(new Set());
    discardPreviews();
    dropFromCount("ready_to_send", done);
  }

  /** The way back for a record of a send from Gmail, on the Sent today tab: the existing clear, one report at a time, two presses. */
  async function clearRecord(id: string) {
    setBusy(true);
    setClearingId(id);
    setBatchError(null);
    try {
      await api.jobsheets.clearSentOutside(id);
      if (!mounted.current) return;
      putResult(id, { ok: true, note: "Record cleared. If this report is still waiting to be sent it is back in Ready to send." });
      dropFromCount("sent_today", 1);
      // A cleared record may put the report back in Ready to send, so that number is read again rather than guessed.
      const fresh = await readReleaseCounts();
      if (mounted.current && fresh) setCounts((c) => ({ ...c, ready_to_send: fresh.reportsReadyToSend }));
    } catch (err) {
      putResult(id, { ok: false, note: friendlyApiError(err, "Could not clear this record. Open the record to see why.") });
    }
    if (mounted.current) {
      setClearingId(null);
      setBusy(false);
    }
  }

  const batchControls = (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" className="h-5 w-5" checked={allTicked} onChange={toggleAll} disabled={busy || selectable.length === 0} aria-label="Tick every visit" />
        Tick all ({selectable.length})
      </label>
      {stage === "awaiting_release" ? (
        <ConfirmButton
          label={`Release ${chosen.length} selected`}
          confirmLabel={`Press again to release ${chosen.length}`}
          busy={busy}
          busyLabel={progress ?? "Releasing..."}
          disabled={chosen.length === 0}
          onConfirm={() => void releaseChosen()}
          className="rounded-ele bg-ele-green px-4 py-2 font-heading text-sm text-ele-bg disabled:opacity-50"
          armedClassName="!bg-ele-purple !text-white"
        />
      ) : previews == null ? (
        <Button onClick={() => void checkRecipients()} disabled={busy || chosen.length === 0}>
          {busy && progress ? progress : `Check recipients for ${chosen.length} selected`}
        </Button>
      ) : (
        <ConfirmButton
          label={`Send ${sendable.length} report${sendable.length === 1 ? "" : "s"} from ${fromAddresses.join(", ") || "the office mailbox"}`}
          confirmLabel={`Press again to send ${sendable.length} to the recipients listed`}
          busy={busy}
          busyLabel={progress ?? "Sending..."}
          disabled={sendable.length === 0}
          onConfirm={() => void sendChosen()}
          className="rounded-ele bg-ele-green px-4 py-2 font-heading text-sm text-ele-bg disabled:opacity-50"
          armedClassName="!bg-ele-purple !text-white"
        />
      )}
      {previews != null && !busy && (
        <Button variant="ghost" onClick={discardPreviews}>
          Change selection
        </Button>
      )}
    </div>
  );

  // DRAWN AFTER THE LIST (review of PR 274, M1), not above it: the person vouches for the addresses each row shows, so those rows must be
  // above the box that records them, not below it and off the screen.
  const recordPanel =
    stage === "ready_to_send" && previews != null ? (
        <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-surface-soft p-3" aria-label="Already sent from Gmail">
          <p className="mb-2 text-sm text-ele-strong">Already sent these from Gmail? Record them instead of sending. Nothing is emailed.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-ele-muted">
              Sent on
              <input
                type="datetime-local"
                value={sentAtLocal}
                max={localInputValue(new Date(Date.now() + 5 * 60_000))}
                onChange={(e) => setSentAtLocal(e.target.value)}
                disabled={busy}
                className="min-h-11 rounded-ele border border-ele-hairline bg-ele-surface px-2 text-sm text-ele-strong"
              />
            </label>
            <ConfirmButton
              label={`Record ${sendable.length} as sent outside Guvnor${sentAtWords ? ` on ${sentAtWords}` : ""}`}
              confirmLabel={`Press again to record ${sendable.length} as sent on ${sentAtWords ?? "that date"}. Nothing is emailed`}
              busy={busy}
              busyLabel={progress ?? "Recording..."}
              disabled={sendable.length === 0 || sentAtWords == null}
              onConfirm={() => void recordOutsideChosen()}
              className="rounded-ele border border-ele-purple px-4 py-2 font-heading text-sm text-ele-strong disabled:opacity-50"
              armedClassName="!bg-ele-purple !text-white"
            />
          </div>
          <p className="mt-2 text-xs text-ele-muted">
            Each report is recorded as sent to the addresses shown on its row above, which are the addresses on file. If a report went to someone else, do not record it here: record it on its own record, where you type the addresses. Recording a report also makes it open on the customer&apos;s Client Portal, if they have one.
          </p>
        </div>
    ) : null;

  /** The part of a row that is the same on a phone and on a desktop: the warnings, the summary, the previewed recipients, the result. */
  function rowDetail(s: QueueRow) {
    const warnings = releaseWarnings(s);
    const res = results[s.jobId];
    const pv = previews?.[s.jobId];
    const summary = summaryText(s);
    const expanded = open.has(s.jobId);
    const held = liveIds.has(s.jobId) && !res?.ok ? batchBlockReason(stage, s) : null;
    return (
      <div className="space-y-1">
        {warnings.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {warnings.map((w) => (
              <StatusPill key={w} word={w} tone={w === "Left out of service" ? "red" : "amber"} />
            ))}
          </div>
        )}
        {held && (
          <p role="status" className="text-xs text-ele-strong">
            {held}
          </p>
        )}
        {summary && (
          <button type="button" className="block text-left text-xs text-ele-body" onClick={() => toggleOpen(s.jobId)} aria-expanded={expanded}>
            {expanded ? summary : summary.length > 140 ? `${summary.slice(0, 140)}... (more)` : summary}
          </button>
        )}
        {expanded && s.defects.length > 0 && (
          <ul className="list-disc pl-5 text-xs text-ele-body">
            {s.defects.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        {pv &&
          (isPreviewError(pv) ? (
            <p className="text-xs text-ele-danger">{pv.error}</p>
          ) : (
            <p className="text-xs text-ele-body">
              To {pv.to.join(", ")}
              {pv.cc.length ? `, copy ${pv.cc.join(", ")}` : ""}
              {pv.alreadySent ? " (already sent, will be skipped)" : ""}
            </p>
          ))}
        {stage === "sent_today" && !res?.ok && (
          <div className="space-y-1">
            <p className="text-xs text-ele-body">{sentTodayLine(s)}</p>
            {s.sentOutside && (
              <ConfirmButton
                label="Clear this record"
                confirmLabel="Press again to clear the record"
                busy={clearingId === s.jobId}
                busyLabel="Clearing..."
                disabled={busy}
                onConfirm={() => void clearRecord(s.jobId)}
                className="min-h-11 rounded-ele border border-ele-hairline px-3 py-1 text-xs text-ele-strong disabled:opacity-50"
                armedClassName="!bg-ele-purple !text-white"
              />
            )}
          </div>
        )}
        {res && (
          <p role="status" className={`text-xs ${res.ok ? "text-ele-green" : "text-ele-danger"}`}>
            {res.note}
          </p>
        )}
        {res?.warnings?.map((w, i) => (
          <p key={i} role="alert" className="rounded-ele border border-ele-amber-line bg-ele-surface p-2 text-xs text-ele-strong">
            {w}
          </p>
        ))}
      </div>
    );
  }

  const tick = (s: QueueRow) => {
    if (stage === "sent_today") return null;
    const ok = selectable.some((r) => r.jobId === s.jobId);
    return (
      <label className="flex min-h-11 min-w-11 items-center justify-center">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={selected.has(s.jobId)}
          onChange={() => toggle(s.jobId)}
          disabled={busy || !ok}
          aria-label={`Tick ${s.reference ?? s.ref}`}
        />
      </label>
    );
  };

  // While a run is in progress the way off the page is inert, so a half-finished batch is not walked away from by accident.
  const linkClass = "inline-flex min-h-11 items-center text-xs text-ele-blue underline";
  const openRecord = (s: QueueRow) =>
    busy ? (
      <span aria-disabled="true" className={`${linkClass} opacity-50`}>
        Open record
      </span>
    ) : (
      <Link to={`/office/jobsheets/${encodeURIComponent(s.jobId)}`} className={linkClass}>
        Open record
      </Link>
    );

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Release and send"
        subtitle={
          stage === "awaiting_release"
            ? "Closed visits waiting for the office. Check each summary, tick, and release in one go. Nothing goes to the customer when you release."
            : stage === "sent_today"
              ? "Reports sent today from Guvnor, and reports you recorded today as sent from Gmail (whatever date you gave). A record entered by mistake can be cleared here."
              : "Released visit reports waiting to be sent. This sends each visit's record or report. A call-out's separate attendance report is released from the call-out page."
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-ele-body" aria-live="polite">
          {[
            counts.awaiting_release != null ? `Awaiting release: ${counts.awaiting_release}.` : null,
            counts.ready_to_send != null ? `Ready to send: ${counts.ready_to_send}.` : null,
            counts.sent_today != null ? `Sent today: ${counts.sent_today}.` : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
        <Button variant="ghost" onClick={() => void load(stage)} disabled={busy}>
          Refresh the list
        </Button>
      </div>

      {batchError && (
        <p role="alert" className="mb-3 text-sm text-ele-danger">
          {batchError}
        </p>
      )}

      {loading ? (
        <Loader />
      ) : loadError ? (
        <p role="alert" className="text-ele-danger">
          The queue could not be loaded. Refresh the page to try again.
        </p>
      ) : withId.length === 0 ? (
        <EmptyState
          message={stage === "awaiting_release" ? "Nothing waiting for release." : stage === "sent_today" ? "Nothing has been sent today." : "Nothing waiting to be sent."}
          hint={
            stage === "awaiting_release"
              ? "Every closed visit has been released."
              : stage === "sent_today"
                ? "Reports sent from Guvnor today, and any report you record today as sent from Gmail (whatever date you gave it), appear here."
                : "Every released report has been sent or recorded as sent outside Guvnor."
          }
        />
      ) : (
        <>
          {stage !== "sent_today" && batchControls}
          {mobile ? (
            <MobileCardList label={stage === "awaiting_release" ? "Visits awaiting release" : stage === "sent_today" ? "Reports sent today" : "Reports ready to send"}>
              {withId.map((s) => (
                <MobileListCard
                  key={s.jobId}
                  primary={
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-ele-loler">{s.reference ?? s.ref}</span>
                      <JobTypeBadge type={s.jobType} size="sm" />
                    </span>
                  }
                  secondary={
                    <>
                      {s.siteName}
                      {s.customerName ? ` · ${s.customerName}` : ""}
                    </>
                  }
                  meta={s.completedAt ? formatDate(s.completedAt) : "No date"}
                  details={rowDetail(s)}
                  actions={
                    <>
                      {tick(s)}
                      {openRecord(s)}
                    </>
                  }
                />
              ))}
            </MobileCardList>
          ) : (
            <ul className="space-y-2" aria-label={stage === "awaiting_release" ? "Visits awaiting release" : stage === "sent_today" ? "Reports sent today" : "Reports ready to send"}>
              {withId.map((s) => (
                <li key={s.jobId} className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-3" data-job={s.jobId}>
                  <div className="flex flex-wrap items-start gap-3">
                    {tick(s)}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm text-ele-loler">{s.reference ?? s.ref}</span>
                        <JobTypeBadge type={s.jobType} size="sm" />
                        <span className="text-xs text-ele-muted">{s.completedAt ? formatDate(s.completedAt) : "No date"}</span>
                      </div>
                      <p className="text-sm text-ele-strong">
                        {s.siteName}
                        {s.customerName ? <span className="text-ele-muted"> · {s.customerName}</span> : null}
                      </p>
                      {rowDetail(s)}
                    </div>
                    {openRecord(s)}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {stage !== "sent_today" && recordPanel}
        </>
      )}
    </div>
  );
}
